import { ArrowRight, Rocket, Heart, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useSEO } from '../hooks/useSEO';

export default function Careers() {
  useSEO({
    title: 'Careers | Join the GreenTick AI Revolution',
    description: 'Join the team building the future of talent assessment. Explore career opportunities at GreenTick and help us revolutionize hiring with AI.',
    canonicalPath: '/careers',
  });
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <Navbar />

      <div className="pt-32 pb-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <Link to="/" className="inline-flex items-center gap-2 text-teal-400 hover:text-teal-300 transition-colors mb-8">
              <span>← Back to Home</span>
            </Link>

            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Join Our
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-emerald-400">
                Growing Team
              </span>
            </h1>

            <p className="text-xl text-slate-300 mb-12 leading-relaxed">
              Be part of the team revolutionizing recruitment with AI. Help us build the future of talent assessment.
            </p>

            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <div className="glass p-8 rounded-2xl text-center">
                <div className="w-16 h-16 bg-teal-500/20 rounded-2xl flex items-center justify-center mb-4 mx-auto">
                  <Rocket className="w-8 h-8 text-teal-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Innovation First</h3>
                <p className="text-slate-400">
                  Work on cutting-edge AI technology that's transforming how companies hire.
                </p>
              </div>

              <div className="glass p-8 rounded-2xl text-center">
                <div className="w-16 h-16 bg-emerald-500/20 rounded-2xl flex items-center justify-center mb-4 mx-auto">
                  <TrendingUp className="w-8 h-8 text-emerald-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Growth Opportunities</h3>
                <p className="text-slate-400">
                  Advance your career in a fast-growing startup with unlimited potential.
                </p>
              </div>

              <div className="glass p-8 rounded-2xl text-center">
                <div className="w-16 h-16 bg-teal-500/20 rounded-2xl flex items-center justify-center mb-4 mx-auto">
                  <Heart className="w-8 h-8 text-teal-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Great Culture</h3>
                <p className="text-slate-400">
                  Join a team that values collaboration, creativity, and work-life balance.
                </p>
              </div>
            </div>

            <div className="glass p-12 rounded-2xl mb-12">
              <h2 className="text-3xl font-bold text-white mb-6">Why Work With Us?</h2>
              <div className="space-y-4 text-slate-300 text-lg">
                <div className="flex gap-4">
                  <div className="w-2 h-2 bg-teal-400 rounded-full mt-3 flex-shrink-0"></div>
                  <p>Competitive compensation and equity opportunities</p>
                </div>
                <div className="flex gap-4">
                  <div className="w-2 h-2 bg-emerald-400 rounded-full mt-3 flex-shrink-0"></div>
                  <p>Flexible work arrangements and remote options</p>
                </div>
                <div className="flex gap-4">
                  <div className="w-2 h-2 bg-teal-400 rounded-full mt-3 flex-shrink-0"></div>
                  <p>Comprehensive health and wellness benefits</p>
                </div>
                <div className="flex gap-4">
                  <div className="w-2 h-2 bg-emerald-400 rounded-full mt-3 flex-shrink-0"></div>
                  <p>Professional development and learning opportunities</p>
                </div>
                <div className="flex gap-4">
                  <div className="w-2 h-2 bg-teal-400 rounded-full mt-3 flex-shrink-0"></div>
                  <p>Work with cutting-edge AI and machine learning technologies</p>
                </div>
                <div className="flex gap-4">
                  <div className="w-2 h-2 bg-emerald-400 rounded-full mt-3 flex-shrink-0"></div>
                  <p>Make a real impact on how companies find and hire talent</p>
                </div>
              </div>
            </div>

            <div className="glass p-12 rounded-2xl mb-12">
              <h2 className="text-3xl font-bold text-white mb-6">Current Openings</h2>
              <p className="text-slate-300 text-lg mb-6">
                We're always looking for talented individuals to join our team. If you're passionate about AI, recruitment, and making a difference, we'd love to hear from you.
              </p>
              <p className="text-slate-400">
                Please send your resume and cover letter to our team through the contact form below.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                className="group px-8 py-4 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-xl font-semibold hover:shadow-2xl hover:shadow-teal-500/50 transition-all hover:scale-105 flex items-center gap-2 justify-center"
              >
                Apply Now
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <a
                href="https://www.linkedin.com/company/hirewithgreentick"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 glass text-white rounded-xl font-semibold hover:bg-white/20 transition-all hover:scale-105 flex items-center justify-center"
              >
                View on LinkedIn
              </a>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
